import { supabase } from "../lib/supabase.js";

/* Client half of the AI Preservation Scanner.

   The photo goes to /.netlify/functions/preservation-scan, never to Anthropic
   directly — the API key lives on the server, because anything the browser
   holds can be read out of dev tools. */

const ENDPOINT = "/.netlify/functions/preservation-scan";

/* Claude downscales images to fit roughly 1568px on the long edge before
   reading them, so anything larger is bandwidth and tokens spent on detail
   that gets thrown away. A modern phone photo is several times this. */
const MAX_EDGE = 1568;
const JPEG_QUALITY = 0.85;

/** Shrinks a photo to something worth uploading, and returns base64 + type.
 *
 *  Falls back to the original file if anything about the canvas path fails —
 *  a scan of a large photo is better than no scan. */
async function prepareImage(file) {
  const asIs = async () => ({
    imageBase64: await toBase64(file),
    mediaType: file.type || "image/jpeg",
  });

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1_500_000) {
      bitmap.close?.();
      return asIs();
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    return { imageBase64: dataUrl.split(",")[1], mediaType: "image/jpeg" };
  } catch {
    return asIs();
  }
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Couldn't read that file."));
    reader.readAsDataURL(file);
  });
}

/** Scans one photo of one item.
 *
 *  Returns { result } or { error }, where error is already a sentence worth
 *  showing someone — the function sends back specific, actionable messages
 *  (an unset key, an expired session, a photo too large) and throwing them
 *  away for a generic "scan failed" is how a fixable problem stays unfixed. */
export async function scanPhoto(file, item) {
  if (!file) return { error: "No photo selected." };

  let payload;
  try {
    payload = await prepareImage(file);
  } catch {
    return { error: "Couldn't read that photo. Try another one." };
  }

  // The scan is charged per call, so it is gated on a real session rather than
  // sent optimistically and rejected server-side.
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { error: "Sign in again to use the scanner." };

  let response;
  try {
    response = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...payload,
        category: item.category,
        brand: item.brand,
        model: item.model,
        colorway: item.colorway,
      }),
    });
  } catch {
    return { error: "Couldn't reach the scanner — check your connection." };
  }

  let body = null;
  try {
    body = await response.json();
  } catch {
    /* A non-JSON body means something upstream of the function answered —
       most often the SPA's index.html, which is what a missing function
       deploy looks like from here. */
    return {
      error:
        response.status === 404
          ? "The scanner isn't deployed on this site yet."
          : "The scanner returned something unreadable.",
    };
  }

  if (!response.ok) return { error: body?.error || "The scan failed." };
  return { result: body };
}

/** Merges a scan into an item's existing materials.
 *
 *  A scan adds what it saw; it does not erase what a collector recorded. The
 *  photo shows one side of one piece, and treating it as the whole truth would
 *  quietly delete the lining they entered by hand from a tag we can't see. */
export function mergeScannedMaterials(existing, identified) {
  const out = { ...(existing || {}) };
  for (const [group, values] of Object.entries(identified || {})) {
    const merged = new Set([...(out[group] || []), ...values]);
    out[group] = [...merged];
  }
  return out;
}
